// apps/app/src/lib/permissions.ts
//
// This hides UI. It is NOT the control — RolesGuard on the API is, and the two
// are deliberately separate. Hidden UI is a courtesy; a guard is a boundary.
//
// Mirrors the role table in docs/04 §1.

import i18n from './i18n';
import type { Role } from './auth';

export type Capability =
  | 'billing.manage'
  | 'payments.connect'
  | 'tenant.delete'
  // Clinic details and the onboarding wizard. Mirrors @Roles('OWNER', 'ADMIN')
  // on PATCH /tenants/me and the onboarding saves.
  | 'clinic.manage'
  // Taking the widget live. POST /tenants/me/go-live is OWNER only.
  | 'tenant.goLive'
  // Changing a colleague's role. OWNER and ADMIN at the API, which also enforces
  // the hierarchy: an admin never grants OWNER or acts on an owner.
  | 'team.roles'
  | 'patients.merge'
  | 'orders.fulfil'
  | 'catalogue.write'
  | 'widget.configure'
  | 'domains.manage'
  | 'team.manage'
  | 'patients.read'
  | 'patients.clinical.write'
  | 'patients.erase'
  | 'bookings.manage'
  | 'bookings.own'
  | 'analytics.read'
  | 'simulations.approve'
  // Operating the SaaS itself. Held only by PLATFORM, never by a clinic role.
  | 'platform.manage'
  | 'platform.verify';

const MATRIX: Record<Role, Capability[]> = {
  // Operates the platform, belongs to no clinic. Deliberately holds none of
  // the clinic capabilities: an operator has no business inside a patient
  // record, and cross-tenant reach is exactly what must not be casual.
  PLATFORM: ['platform.manage', 'platform.verify'],
  OWNER: [
    'clinic.manage',
    'tenant.goLive',
    'team.roles',
    'patients.merge',
    'orders.fulfil',
    'billing.manage',
    'payments.connect',
    'tenant.delete',
    'catalogue.write',
    'widget.configure',
    'domains.manage',
    'team.manage',
    'patients.read',
    'patients.clinical.write',
    'patients.erase',
    'bookings.manage',
    'analytics.read',
    'simulations.approve',
  ],
  ADMIN: [
    'clinic.manage',
    'team.roles',
    'patients.merge',
    'orders.fulfil',
    'catalogue.write',
    'widget.configure',
    'domains.manage',
    'team.manage',
    'patients.read',
    'patients.clinical.write',
    'patients.erase',
    'bookings.manage',
    'analytics.read',
    'simulations.approve',
  ],
  // Scoped to their own calendar. A clinic with four injectors will not accept
  // everyone seeing everyone's schedule.
  PRACTITIONER: [
    'patients.read',
    'patients.clinical.write',
    'bookings.own',
    'analytics.read',
    'simulations.approve',
  ],
  // No config, no analytics, no financials.
  FRONT_DESK: ['patients.read', 'bookings.manage', 'orders.fulfil'],
};

export function can(role: Role | undefined, capability: Capability): boolean {
  return role ? MATRIX[role].includes(capability) : false;
}

/**
 * Why an action is unavailable, for the tooltip on a disabled control.
 * "Connect Stripe to enable deposits" beats a greyed-out button with no reason.
 */
export function whyDisabled(role: Role | undefined, capability: Capability): string | null {
  if (can(role, capability)) return null;
  if (capability.startsWith('platform.')) return i18n.t('common:permissions.why.platform');
  if (capability === 'billing.manage') return i18n.t('common:permissions.why.billing');
  if (capability === 'bookings.own') return i18n.t('common:permissions.why.ownCalendar');
  return i18n.t('common:permissions.why.role');
}

/** The roles a clinic can hold. PLATFORM is not one of them. */
export type ClinicRole = Exclude<Role, 'PLATFORM'>;

export const CLINIC_ROLES: ClinicRole[] = ['OWNER', 'ADMIN', 'PRACTITIONER', 'FRONT_DESK'];

/**
 * `labelKey` and `summaryKey` are namespaced keys (`common:…`), so any
 * screen's own `t` translates them: `t(ROLE_INFO[role].labelKey)`. The English
 * `label` and `summary` stay for screens not yet translated.
 */
export const ROLE_INFO: Record<ClinicRole, { label: string; summary: string; labelKey: string; summaryKey: string }> = {
  OWNER: {
    label: 'Owner',
    summary: 'Everything, including billing, payouts and deleting the clinic.',
    labelKey: 'common:roles.owner.label',
    summaryKey: 'common:roles.owner.summary',
  },
  ADMIN: {
    label: 'Admin',
    summary: 'Runs the clinic day to day: team, catalogue, widget, patients. No billing.',
    labelKey: 'common:roles.admin.label',
    summaryKey: 'common:roles.admin.summary',
  },
  PRACTITIONER: {
    label: 'Practitioner',
    summary: 'Their own calendar and patients, including clinical details.',
    labelKey: 'common:roles.practitioner.label',
    summaryKey: 'common:roles.practitioner.summary',
  },
  FRONT_DESK: {
    label: 'Front desk',
    summary: 'Bookings, patients and orders. No configuration, no financials.',
    labelKey: 'common:roles.frontDesk.label',
    summaryKey: 'common:roles.frontDesk.summary',
  },
};

/**
 * The capabilities a clinic role can hold, in words a clinic owner would use.
 * Drives the "what this role can do" lists, so they cannot drift from what the
 * matrix above actually grants. `titleKey` and `labelKey` are namespaced keys
 * (`common:…`) for any screen's `t`; the English text stays alongside.
 */
export const CAPABILITY_GROUPS: {
  title: string;
  titleKey: string;
  items: { capability: Capability; label: string; labelKey: string }[];
}[] = [
  {
    title: 'Patients',
    titleKey: 'common:permissions.groups.patients',
    items: [
      { capability: 'patients.read', label: 'View patients and scans', labelKey: 'common:permissions.capabilities.patientsRead' },
      { capability: 'patients.clinical.write', label: 'Edit clinical details', labelKey: 'common:permissions.capabilities.patientsClinicalWrite' },
      { capability: 'patients.merge', label: 'Merge duplicate records', labelKey: 'common:permissions.capabilities.patientsMerge' },
      { capability: 'patients.erase', label: "Erase a patient's data", labelKey: 'common:permissions.capabilities.patientsErase' },
    ],
  },
  {
    title: 'Bookings and orders',
    titleKey: 'common:permissions.groups.bookingsOrders',
    items: [
      { capability: 'bookings.manage', label: 'Manage every calendar', labelKey: 'common:permissions.capabilities.bookingsManage' },
      { capability: 'bookings.own', label: 'Manage their own calendar', labelKey: 'common:permissions.capabilities.bookingsOwn' },
      { capability: 'orders.fulfil', label: 'Update order status', labelKey: 'common:permissions.capabilities.ordersFulfil' },
    ],
  },
  {
    title: 'Catalogue and growth',
    titleKey: 'common:permissions.groups.catalogueGrowth',
    items: [
      { capability: 'catalogue.write', label: 'Edit treatments and products', labelKey: 'common:permissions.capabilities.catalogueWrite' },
      { capability: 'widget.configure', label: 'Configure and install the widget', labelKey: 'common:permissions.capabilities.widgetConfigure' },
      { capability: 'analytics.read', label: 'View analytics', labelKey: 'common:permissions.capabilities.analyticsRead' },
      { capability: 'simulations.approve', label: 'Approve simulations', labelKey: 'common:permissions.capabilities.simulationsApprove' },
    ],
  },
  {
    title: 'Clinic',
    titleKey: 'common:permissions.groups.clinic',
    items: [
      { capability: 'clinic.manage', label: 'Edit clinic details', labelKey: 'common:permissions.capabilities.clinicManage' },
      { capability: 'team.manage', label: 'Invite and remove team members', labelKey: 'common:permissions.capabilities.teamManage' },
      { capability: 'team.roles', label: "Change team members' roles", labelKey: 'common:permissions.capabilities.teamRoles' },
      { capability: 'domains.manage', label: 'Manage domains', labelKey: 'common:permissions.capabilities.domainsManage' },
      { capability: 'billing.manage', label: 'Manage plan and billing', labelKey: 'common:permissions.capabilities.billingManage' },
      { capability: 'payments.connect', label: 'Connect payouts', labelKey: 'common:permissions.capabilities.paymentsConnect' },
      { capability: 'tenant.goLive', label: 'Take the widget live', labelKey: 'common:permissions.capabilities.tenantGoLive' },
      { capability: 'tenant.delete', label: 'Delete the clinic', labelKey: 'common:permissions.capabilities.tenantDelete' },
    ],
  },
];

/**
 * The roles this person may hand out. Mirrors the API: an owner grants any
 * role; an admin grants anything but OWNER.
 */
export function assignableRoles(actor: Role | undefined): ClinicRole[] {
  if (actor === 'OWNER') return CLINIC_ROLES;
  if (actor === 'ADMIN') return CLINIC_ROLES.filter((r) => r !== 'OWNER');
  return [];
}

/** Whether this person may change or remove a member holding `target`. */
export function canManageMember(actor: Role | undefined, target: Role): boolean {
  if (!can(actor, 'team.manage')) return false;
  return actor === 'OWNER' || target !== 'OWNER';
}
